import { useState, useCallback, useRef, Children } from 'react';
import { KEYS } from '../navigation.constants';
import { useFocusItem } from '../use-focus-item';

// Direction constants
export const DIRECTION = {
  NEXT: 'next',
  PREV: 'prev',
  FIRST: 'first',
  LAST: 'last',
};

export function useListNavigation({
  children,
  orientation = 'vertical',
  loop = true,
  disabled = false,
  onItemActivate = null,
  onItemFocus = null,
}) {
  const containerRef = useRef(null);
  const items = Children.toArray(children);
  const itemCount = items.length;

  // Helper to check if an item should be skipped during navigation
  const isItemSkippable = useCallback(
    index => {
      if (index < 0 || index >= itemCount) return true;
      const item = items[index];
      if (!item?.props) return false;
      return item.props.disabled;
    },
    [items, itemCount],
  );

  // Unified helper to find next focusable item in any direction
  const findNextFocusableIndex = useCallback(
    (startIndex, step) => {
      for (let i = 0; i < itemCount; i++) {
        const index = startIndex + step * (i + 1);
        const wrappedIndex = loop
          ? ((index % itemCount) + itemCount) % itemCount
          : Math.max(0, Math.min(index, itemCount - 1));

        if (!isItemSkippable(wrappedIndex)) return wrappedIndex;
        if (!loop && (wrappedIndex === 0 || wrappedIndex === itemCount - 1)) break;
      }
      return Math.max(0, startIndex);
    },
    [itemCount, loop, isItemSkippable],
  );

  // Find first focusable item for initial state
  const [focusIndex, setFocusIndex] = useState(() => {
    const index = items.findIndex(item => !item?.props?.disabled);
    return index !== -1 ? index : 0;
  });

  const { focusItem, getCurrentFocusIndex } = useFocusItem({
    containerRef,
    itemCount,
    items,
    setFocusIndex,
    focusIndex,
    onItemFocus,
  });

  const getNextIndex = useCallback(
    (currentIndex, direction) => {
      if (itemCount === 0) return 0;

      switch (direction) {
        case DIRECTION.NEXT:
          return findNextFocusableIndex(currentIndex, 1);
        case DIRECTION.PREV:
          return findNextFocusableIndex(currentIndex, -1);
        case DIRECTION.FIRST:
          return findNextFocusableIndex(-1, 1);
        case DIRECTION.LAST:
          return findNextFocusableIndex(itemCount, -1);
        default:
          return currentIndex;
      }
    },
    [itemCount, findNextFocusableIndex],
  );

  const handleKeyDown = useCallback(
    event => {
      if (disabled || itemCount === 0) return;

      const { key } = event;
      const isVertical = orientation === 'vertical';
      let handled = false;
      let direction = null;

      // Determine direction based on key and orientation
      if (isVertical) {
        switch (key) {
          case KEYS.ARROW_DOWN:
            direction = DIRECTION.NEXT;
            handled = true;
            break;
          case KEYS.ARROW_UP:
            direction = DIRECTION.PREV;
            handled = true;
            break;
          default:
            break;
        }
      } else {
        switch (key) {
          case KEYS.ARROW_RIGHT:
            direction = DIRECTION.NEXT;
            handled = true;
            break;
          case KEYS.ARROW_LEFT:
            direction = DIRECTION.PREV;
            handled = true;
            break;
          default:
            break;
        }
      }

      // Common navigation keys
      switch (key) {
        case KEYS.HOME:
          direction = DIRECTION.FIRST;
          handled = true;
          break;
        case KEYS.END:
          direction = DIRECTION.LAST;
          handled = true;
          break;
        case KEYS.ENTER:
        case KEYS.SPACE:
          if (onItemActivate) {
            const currentIndex = getCurrentFocusIndex();
            if (items[currentIndex]) {
              event.preventDefault();
              event.stopPropagation();
              onItemActivate(items[currentIndex], currentIndex);
              handled = true;
            }
          }
          break;
        default:
          break;
      }

      if (handled && direction) {
        event.preventDefault();
        event.stopPropagation();
        const currentIndex = getCurrentFocusIndex();
        const nextIndex = getNextIndex(currentIndex, direction);
        focusItem(nextIndex);
      } else if (handled) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [
      disabled,
      itemCount,
      orientation,
      getCurrentFocusIndex,
      getNextIndex,
      focusItem,
      onItemActivate,
      items,
    ],
  );

  const handleFocus = useCallback(
    index => {
      if (disabled) return;
      setFocusIndex(index);
      if (onItemFocus) {
        onItemFocus(items[index], index);
      }
    },
    [disabled, items, onItemFocus],
  );

  const getItemProps = useCallback(
    index => ({
      tabIndex: index === focusIndex ? 0 : -1,
      onFocus: () => handleFocus(index),
      'data-nav-index': index,
      role: 'listitem',
    }),
    [focusIndex, handleFocus],
  );

  const getContainerProps = useCallback(
    () => ({
      ref: containerRef,
      onKeyDown: handleKeyDown,
      role: 'list',
      'aria-orientation': orientation,
      'data-navigable-container': true,
    }),
    [handleKeyDown, orientation],
  );

  return {
    focusIndex,
    items,
    getItemProps,
    getContainerProps,
    focusItem,
  };
}
